import { CalculationHelpers } from '../../_common/client/calculation-helpers';
import { decodeHtml } from '../../_common/client/decode-html';
import { trackEvent } from '@otto-ec/nexus-registry/pdp/tracking';
import { InstallmentConfig } from '../../_common/types/installment-plan-calculator';
import { getPriceByBenefitId, getRetailPrice } from '../../_common/client/installment-plan';
import { calcInstallmentRate } from '../../_common/server/installment-plan';
import { benefitEvents } from '@otto-ec/nexus-registry/benefit/events';

const SELECTOR_SHEET = '.js_pdp_installment-plan-calculator__sheet';
const SELECTOR_DURATION_SELECTION_INPUT_LIST = `${SELECTOR_SHEET}__duration-selection`;
const SELECTOR_DURATION_SELECTION_INPUT_LIST_ITEM = `${SELECTOR_SHEET}__duration-value`;
const SELECTOR_INSTALLMENT = `${SELECTOR_SHEET}__installment`;

export class InstallmentPlanCalculatorSheet {
    private calculatorElement: HTMLElement = null as unknown as HTMLElement;
    private price = 0;
    private benefitId?: string;
    private isInitialized = false;

    constructor() {
        benefitEvents.active.subscribe(benefit => {
            if (benefit.id && benefit.active) {
                this.benefitId = benefit.id;
            }
        });
    }

    public onSheetOpen(event: Event) {
        if (event && event.target instanceof HTMLElement) {
            const calculatorElement = InstallmentPlanCalculatorSheet.calculatorContent(event.target);
            if (calculatorElement) {
                this.calculatorElement = calculatorElement;
                this.initialize();
            }
        }
    }

    private initialize() {
        if (this.isInitialized) {
            return;
        }
        this.isInitialized = true;
        const retailPrice = getRetailPrice(this.calculatorElement);
        if (!retailPrice) {
            throw new Error('no retail price found on data attribute');
        }
        this.price = !this.benefitId ? retailPrice : getPriceByBenefitId(this.calculatorElement, this.benefitId);
        if (!this.price) {
            throw new Error('no price could be evaluated for calculating installment rates');
        }

        const defaultDuration = this.extractDefaultDurationFromHtml();
        const defaultDurationInput = this.getDefaultDurationInputElement(defaultDuration);
        this.checkDefaultDurationInput(defaultDurationInput);
        const defaultInstallmentConfig = this.readInstallmentConfig(defaultDurationInput);

        this.triggerCalculation(defaultInstallmentConfig);

        trackEvent({
            product_InstallmentCalculator: ['open'],
            product_InstallmentCalculatorRate: [String(defaultDuration)],
        });

        this.registerDurationClickListeners();
    }

    private extractDefaultDurationFromHtml(): number {
        const defaultDurationContainer = this.calculatorElement.querySelector(SELECTOR_DURATION_SELECTION_INPUT_LIST);
        if (!defaultDurationContainer) {
            throw new Error('duration selection input list does not exist in dom');
        }

        const defaultDuration = defaultDurationContainer.getAttribute('data-default-duration');
        if (!defaultDuration) {
            throw new Error('could not find default duration attribute on duration selection input list dom element');
        }
        return +defaultDuration;
    }

    private getDefaultDurationInputElement(defaultDuration: number): HTMLInputElement {
        const defaultDurationInputSelector = `${SELECTOR_DURATION_SELECTION_INPUT_LIST_ITEM} input[value="${defaultDuration}"]`;
        const defaultDurationInput = this.calculatorElement.querySelector<HTMLInputElement>(defaultDurationInputSelector);
        if (!defaultDurationInput) {
            throw new Error('could not find duration selection input for default duration');
        }
        return defaultDurationInput;
    }

    private checkDefaultDurationInput(defaultDurationInput: HTMLInputElement) {
        defaultDurationInput.checked = true;
    }

    private readInstallmentConfig(inputElement: Element) {
        const escapedInstallmentConfig = inputElement?.getAttribute('data-installment-config');
        if (!escapedInstallmentConfig) {
            throw new Error('could not find data-installment-config attribute on duration input element');
        }
        try {
            return JSON.parse(decodeHtml(escapedInstallmentConfig));
        } catch (_e) {
            throw new Error('could not parse json from data-installment-config attribute on duration input element');
        }
    }

    private registerDurationClickListeners() {
        const durationSelectionInputs = Array.from(this.calculatorElement.querySelectorAll(SELECTOR_DURATION_SELECTION_INPUT_LIST_ITEM));
        for (const durationSelectionInput of durationSelectionInputs) {
            const inputElement = durationSelectionInput?.firstElementChild as HTMLInputElement;
            if (inputElement) {
                const installmentConfig = this.readInstallmentConfig(inputElement);
                inputElement.addEventListener('change', (event: Event) => {
                    event.stopPropagation();
                    this.handleDurationSelectionChange(installmentConfig);
                });
            }
        }
    }

    private handleDurationSelectionChange(installmentConfig: InstallmentConfig) {
        this.triggerCalculation(installmentConfig);
        trackEvent({
            product_InstallmentCalculator: ['changerate'],
            product_InstallmentCalculatorRate: [String(installmentConfig.duration)],
        });
    }

    private triggerCalculation(installmentConfig: InstallmentConfig) {
        const monthlyRate = this.calculateMonthlyRate(installmentConfig);
        const monthlyRateFormatted = this.formatPrice(monthlyRate);
        this.setPrice(monthlyRateFormatted);
    }

    private setPrice(initialPriceFormatted: string) {
        const priceDisplayElement = this.calculatorElement.querySelector(SELECTOR_INSTALLMENT);
        if (!priceDisplayElement) {
            throw new Error('price display element does not exist');
        }
        priceDisplayElement.innerHTML = initialPriceFormatted;
    }

    private formatPrice(price: number) {
        return `${CalculationHelpers.convertFloatToString(price)} €`;
    }

    private calculateMonthlyRate(installmentConfig: InstallmentConfig) {
        return calcInstallmentRate(installmentConfig, this.price);
    }

    private static calculatorContent(htmlElement: HTMLElement) {
        return htmlElement.querySelector<HTMLElement>(SELECTOR_SHEET);
    }
}
